import Phaser from "phaser";
import { DIFFICULTIES, SCENARIOS } from "../config/campaign";
import { MAPS } from "../config/map";
import { loadSave } from "../systems/save";
import { describeScenarioRequirement, isScenarioUnlocked } from "../systems/progression";
import type { DifficultyId } from "../domain/types";
import { addButton, addCastleBackdrop, addLittleCastleFooter, COLORS, title } from "./shared";

const DIFFICULTY_ORDER: readonly DifficultyId[] = ["easy", "medium", "hard"];
const MAPS_PER_PAGE = 4;

export class LevelSelectScene extends Phaser.Scene {
  private page = 0;
  private pageObjects: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("LevelSelect");
  }

  create(): void {
    addCastleBackdrop(this);
    this.cameras.main.setBackgroundColor(COLORS.ink);
    title(
      this,
      "Choose the Battleground",
      "Choose a map and difficulty. Easy: 50 waves • Medium: 75 • Hard: 100.",
    );

    addButton(this, 95, 42, 150, "‹ Back", () => this.scene.start("MainMenu"));
    this.renderPage();
    addLittleCastleFooter(this, "Royal War Table");
  }

  private renderPage(): void {
    this.pageObjects.forEach((object) => object.destroy());
    this.pageObjects = [];

    const maps = Object.values(MAPS);
    const save = loadSave();
    const pageCount = Math.ceil(maps.length / MAPS_PER_PAGE);
    this.page = Phaser.Math.Clamp(this.page, 0, Math.max(0, pageCount - 1));

    const pageMaps = maps.slice(
      this.page * MAPS_PER_PAGE,
      this.page * MAPS_PER_PAGE + MAPS_PER_PAGE,
    );

    const availableWidth = Math.max(320, this.scale.width - 56);
    const columns = this.scale.width >= 1100 ? 4 : this.scale.width >= 680 ? 2 : 1;
    const gap = 16;
    const cardWidth = Math.min(280, (availableWidth - gap * (columns - 1)) / columns);
    const cardHeight = 278;
    const rows = Math.ceil(pageMaps.length / columns);
    const gridWidth = columns * cardWidth + (columns - 1) * gap;
    const gridHeight = rows * cardHeight + (rows - 1) * gap;
    const startX = (this.scale.width - gridWidth) / 2 + cardWidth / 2;
    const top = Math.max(154, (this.scale.height - gridHeight) / 2 + 30);
    const startY = top + cardHeight / 2;

    pageMaps.forEach((map, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);

      const panel = this.add
        .rectangle(x, y, cardWidth, cardHeight, COLORS.panel)
        .setStrokeStyle(3, 0x8f7440);
      const routeMarker = this.add.circle(x, y - 132, 13, 0x2f5fa7).setStrokeStyle(3, 0xf3c656);
      const routeIndex = this.add
        .text(x, y - 132, `${this.page * MAPS_PER_PAGE + index + 1}`, {
          fontFamily: "Georgia, serif",
          fontSize: "12px",
          fontStyle: "bold",
          color: "#fff3cf",
        })
        .setOrigin(0.5);
      this.pageObjects.push(panel, routeMarker, routeIndex);

      const mapName = this.add
        .text(x, y - 108, map.displayName.toUpperCase(), {
          fontFamily: "Georgia, serif",
          fontSize: "15px",
          fontStyle: "bold",
          color: "#fff3cf",
          align: "center",
          wordWrap: { width: cardWidth - 28 },
        })
        .setOrigin(0.5);
      this.pageObjects.push(mapName);

      const mapDetails = this.add
        .text(x, y - 73, `${map.width} × ${map.height}\n${map.description}`, {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: "11px",
          color: "#d9ccb2",
          align: "center",
          wordWrap: { width: cardWidth - 32 },
        })
        .setOrigin(0.5);
      this.pageObjects.push(mapDetails);

      DIFFICULTY_ORDER.forEach((difficultyId, difficultyIndex) => {
        const difficulty = DIFFICULTIES[difficultyId];
        const scenario = SCENARIOS.find(
          (entry) => entry.mapId === map.id && entry.difficulty === difficultyId,
        );
        if (!scenario) return;

        const buttonY = y - 12 + difficultyIndex * 58;
        const unlocked = isScenarioUnlocked(scenario.id, save.progress);
        const stars = save.progress.scenarioStars?.[scenario.id] ?? 0;
        const button = addButton(
          this,
          x,
          buttonY,
          cardWidth - 28,
          unlocked
            ? `${scenario.displayName} • ${difficulty.waveCount} waves${stars > 0 ? ` • ${"★".repeat(stars)}` : ""}\n${scenario.startingLives} lives • ◆${scenario.startingCurrency}`
            : `🔒 ${scenario.displayName}\n${describeScenarioRequirement(scenario.id, save.progress)}`,
          () => {
            if (!unlocked) return;
            this.registry.set("selected-map-id", map.id);
            this.registry.set("selected-scenario-id", scenario.id);
            this.registry.set("selected-difficulty-id", difficultyId);
            this.scene.start("Loadout");
          },
          unlocked && difficultyId === "easy",
        );
        if (!unlocked) {
          button.disableInteractive();
          button.setAlpha(0.48);
        }
        this.pageObjects.push(button);
      });
    });

    if (pageCount > 1) {
      const navigationY = this.scale.height - 38;
      const previous = addButton(
        this,
        this.scale.width / 2 - 155,
        navigationY,
        120,
        "‹ Previous",
        () => {
          this.page -= 1;
          this.renderPage();
        },
      );
      previous.setVisible(this.page > 0);
      this.pageObjects.push(previous);

      const pageLabel = this.add
        .text(this.scale.width / 2, navigationY, `Page ${this.page + 1} of ${pageCount}`, {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: "14px",
          color: "#fff3cf",
        })
        .setOrigin(0.5);
      this.pageObjects.push(pageLabel);

      const next = addButton(this, this.scale.width / 2 + 155, navigationY, 120, "Next ›", () => {
        this.page += 1;
        this.renderPage();
      });
      next.setVisible(this.page < pageCount - 1);
      this.pageObjects.push(next);
    }
  }
}
